import { useCallback, useRef } from "react";
import * as THREE from "three";
import type { Scene } from "three";

export const CONTENT_GROUP_NAME = "viewer-content";

// Objects flagged with this in userData (and their descendants) are skipped when measuring the
// content bounds. Overlays anchored outside the geometry — the placement gizmo, which sits past
// the frame edge — must not inflate the camera-fit sphere, or the fit zooms out to include the
// widget and the actual content loads "out of frame".
export const EXCLUDE_FROM_CONTENT_BOUNDS = "excludeFromContentBounds";

function isExcluded(obj: THREE.Object3D): boolean {
  for (let o: THREE.Object3D | null = obj; o; o = o.parent) {
    if (o.userData?.[EXCLUDE_FROM_CONTENT_BOUNDS]) return true;
  }
  return false;
}

// Returns a getter for the content bounding sphere. The sphere is reused across
// calls to avoid per-frame allocation, so read radius/center immediately rather
// than retaining the instance. Radius is 0 when no content is present.
//
// Points and lines count as content: a slice and a tool path are drawn as `points` and
// `lineSegments`, so measuring meshes alone reported nothing on those steps.
export function useContentBounds() {
  const box = useRef(new THREE.Box3());
  const sphere = useRef(new THREE.Sphere());
  const meshBox = useRef(new THREE.Box3());

  return useCallback((scene: Scene) => {
    const content = scene.getObjectByName(CONTENT_GROUP_NAME);
    box.current.makeEmpty();
    if (content) {
      content.updateWorldMatrix(true, false);
      content.traverse((obj) => {
        const drawable = obj as THREE.Mesh & THREE.Points & THREE.Line;
        if (!drawable.isMesh && !drawable.isPoints && !drawable.isLine) return;
        if (!drawable.geometry) return;
        if (isExcluded(drawable)) return;
        if (!drawable.geometry.boundingBox) drawable.geometry.computeBoundingBox();
        const geoBox = drawable.geometry.boundingBox;
        if (!geoBox) return;
        meshBox.current.copy(geoBox).applyMatrix4(drawable.matrixWorld);
        box.current.union(meshBox.current);
      });
    }

    if (box.current.isEmpty()) {
      sphere.current.center.set(0, 0, 0);
      sphere.current.radius = 0;
      return sphere.current;
    }
    return box.current.getBoundingSphere(sphere.current);
  }, []);
}
